import {
  createParser,
  createSerializer,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'
import type { Values } from 'nuqs'
import type { components } from '@joyful/openapi/types.gen'
import { PracticeRequestTypeSchema } from '../../../../core/components/practice-request/PracticeRequestTypeTag'
import { PracticeRequestOwnerSchema } from '../components/PracticeRequestOwnerBadge'
import { cleanupStaleFacetValues } from '../../filtering/filterBuilders'
import { createEnumSchema, parseAsUuid } from '@joyful/utils'

const PracticeRequestListStatusOrder = [
  'DRAFT',
  'PRE_PUBLISHED',
  'OPEN',
  'CLOSED',
] as const

export type PracticeRequestListStatus =
  (typeof PracticeRequestListStatusOrder)[number]

type PracticeRequestType = components['schemas']['PracticeRequestType']
type PracticeRequestOwner = components['schemas']['PracticeRequestOwner']

export const NeedsJoyfulResponseOwners = [
  'JOYFUL_HUMAN',
  'JOYFUL_AGENT',
] as const satisfies ReadonlyArray<PracticeRequestOwner>

export const NeedsPracticeResponseOwners = [
  'PRACTICE',
] as const satisfies ReadonlyArray<PracticeRequestOwner>

export const AssignedToJoyfulHumanOwners = [
  'JOYFUL_HUMAN',
] as const satisfies ReadonlyArray<PracticeRequestOwner>

export const PracticeRequestListSortKeySchema = createEnumSchema<
  components['schemas']['PracticeRequestListSortKey']
>([
  { title: 'Status', value: 'status' },
  { title: 'Type', value: 'request_type' },
  { title: 'Outstanding', value: 'outstanding_charged_amount' },
  { title: 'Visibility', value: 'visibility' },
  { title: 'Created', value: 'created_at' },
  { title: 'Last activity', value: 'last_activity_at' },
  { title: 'Expires', value: 'expiration_date' },
])

function parseAsClampedInteger({
  defaultValue,
  maximumValue,
}: {
  defaultValue: number
  maximumValue?: number
}) {
  return createParser({
    parse: (value) => {
      const parsed = parseAsInteger.parse(value)
      if (parsed === null) {
        return null
      }

      const lowerBoundedValue = Math.max(parsed, 1)
      return maximumValue
        ? Math.min(lowerBoundedValue, maximumValue)
        : lowerBoundedValue
    },
    serialize: (value) => {
      return String(value)
    },
  }).withDefault(defaultValue)
}

export const PracticeRequestsListQueryMap = {
  assigned_to_me: parseAsBoolean.withDefault(false),
  include_closed: parseAsBoolean.withDefault(false),
  limit: parseAsClampedInteger({ defaultValue: 50, maximumValue: 100 }),
  needs_response: parseAsBoolean.withDefault(false),
  owner: parseAsArrayOf(
    parseAsStringLiteral(PracticeRequestOwnerSchema.values()),
  ).withDefault([]),
  page: parseAsClampedInteger({ defaultValue: 1 }),
  patient_ids: parseAsArrayOf(parseAsUuid).withDefault([]),
  publisher_user_ids: parseAsArrayOf(parseAsUuid).withDefault([]),
  query: parseAsString.withDefault(''),
  request_type: parseAsArrayOf(
    parseAsStringLiteral(PracticeRequestTypeSchema.values()),
  ).withDefault([]),
  snoozed: parseAsBoolean.withDefault(false),
  sort_key: parseAsStringLiteral(PracticeRequestListSortKeySchema.values()),
  sort_order: parseAsStringLiteral(['asc', 'desc'] as const).withDefault(
    'desc',
  ),
  status: parseAsArrayOf(
    parseAsStringLiteral(PracticeRequestListStatusOrder),
  ).withDefault([]),
  themes: parseAsArrayOf(parseAsString).withDefault([]),
  uncollectible_recommendation: parseAsBoolean.withDefault(false),
}

const PracticeRequestsOverlayQueryMap = {
  create: parseAsBoolean.withDefault(false),
  settings: parseAsBoolean.withDefault(false),
}

export const PracticeRequestsRouteQueryMap = {
  ...PracticeRequestsListQueryMap,
  ...PracticeRequestsOverlayQueryMap,
}

export type PracticeRequestsRouteQueryParams = Values<
  typeof PracticeRequestsRouteQueryMap
>

type PracticeRequestPatientId =
  PracticeRequestsRouteQueryParams['patient_ids'][number]

type PracticeRequestPublisherUserId =
  PracticeRequestsRouteQueryParams['publisher_user_ids'][number]

export const EmptyPracticeRequestsListParams: PracticeRequestsRouteQueryParams =
  {
    assigned_to_me: false,
    create: false,
    include_closed: false,
    limit: 50,
    needs_response: false,
    owner: [],
    page: 1,
    patient_ids: [],
    publisher_user_ids: [],
    query: '',
    request_type: [],
    settings: false,
    snoozed: false,
    sort_key: null,
    sort_order: 'desc',
    status: [],
    themes: [],
    uncollectible_recommendation: false,
  }

export type PracticeRequestListDraftListing = 'hidden' | 'on_filter'

function defaultPracticeRequestListStatuses(): Array<PracticeRequestListStatus> {
  return ['PRE_PUBLISHED', 'OPEN']
}

/** Adds or removes one value, keeping the result in the canonical filter order. */
function toggleOrderedFilterValue<Value>({
  order,
  selectedValues,
  value,
}: {
  order: ReadonlyArray<Value>
  selectedValues: ReadonlyArray<Value>
  value: Value
}): Array<Value> {
  const nextValues = selectedValues.includes(value)
    ? selectedValues.filter((selectedValue) => {
        return selectedValue !== value
      })
    : [...selectedValues, value]
  return order.filter((orderedValue) => {
    return nextValues.includes(orderedValue)
  })
}

/** Statuses the list should show for the current URL and viewer. */
export function resolveEffectivePracticeRequestListStatuses({
  draftListing,
  requestedStatuses,
}: {
  draftListing: PracticeRequestListDraftListing
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
}): Array<PracticeRequestListStatus> {
  const listableStatuses = PracticeRequestListStatusOrder.filter((status) => {
    const isHiddenDraft = status === 'DRAFT' && draftListing === 'hidden'
    return requestedStatuses.includes(status) && !isHiddenDraft
  })
  if (listableStatuses.length > 0) {
    return listableStatuses
  }

  return defaultPracticeRequestListStatuses()
}

/** True when the list is scoped to drafts only via the Drafts quick filter. */
export function isPracticeRequestListDraftsFilterActive({
  draftListing,
  owners,
  requestedStatuses,
  snoozed,
}: {
  draftListing: PracticeRequestListDraftListing
  owners: ReadonlyArray<PracticeRequestOwner>
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
  snoozed: boolean
}): boolean {
  if (draftListing === 'hidden' || owners.length > 0 || snoozed) {
    return false
  }

  return requestedStatuses.length === 1 && requestedStatuses.at(0) === 'DRAFT'
}

/** Keep a non-default status selection in the URL, otherwise clear it. */
export function normalizePracticeRequestListStatusFilter(
  statuses: ReadonlyArray<PracticeRequestListStatus>,
): Array<PracticeRequestListStatus> {
  const orderedStatuses = PracticeRequestListStatusOrder.filter((status) => {
    return statuses.includes(status)
  })
  const defaultStatuses = defaultPracticeRequestListStatuses()
  const isDefaultSelection = orderedStatuses.join() === defaultStatuses.join()
  return isDefaultSelection ? [] : orderedStatuses
}

/** Add or remove one request type from the current list filter. */
export function togglePracticeRequestListTypeFilter({
  requestType,
  requestedTypes,
}: {
  requestType: PracticeRequestType
  requestedTypes: ReadonlyArray<PracticeRequestType>
}): Array<PracticeRequestType> {
  return toggleOrderedFilterValue({
    order: PracticeRequestTypeSchema.values(),
    selectedValues: requestedTypes,
    value: requestType,
  })
}

/** Add or remove one status from the current list filter. */
export function togglePracticeRequestListStatusFilter({
  draftListing,
  requestedStatuses,
  status,
}: {
  draftListing: PracticeRequestListDraftListing
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
  status: PracticeRequestListStatus
}): Array<PracticeRequestListStatus> {
  const nextStatuses = toggleOrderedFilterValue({
    order: PracticeRequestListStatusOrder,
    selectedValues: resolveEffectivePracticeRequestListStatuses({
      draftListing,
      requestedStatuses,
    }),
    value: status,
  })
  return normalizePracticeRequestListStatusFilter(nextStatuses)
}

/** List and facet query fields for the selected statuses. */
export function practiceRequestListStatusQueryParams({
  draftListing,
  includeClosed = false,
  requestedStatuses,
}: {
  draftListing: PracticeRequestListDraftListing
  includeClosed?: boolean
  requestedStatuses: ReadonlyArray<PracticeRequestListStatus>
}) {
  if (requestedStatuses.length > 0) {
    return { statuses: [...requestedStatuses] }
  }

  if (draftListing === 'on_filter') {
    const statuses: Array<PracticeRequestListStatus> = includeClosed
      ? ['PRE_PUBLISHED', 'OPEN', 'CLOSED']
      : ['PRE_PUBLISHED', 'OPEN']
    return { statuses }
  }

  return { include_closed: includeClosed }
}

export interface PracticeRequestFacetValuesState {
  isAuthorizedBaseScopeResponse: boolean
  isPlaceholderData: boolean
  patientIds: ReadonlyArray<PracticeRequestPatientId>
  publisherUserIds: ReadonlyArray<PracticeRequestPublisherUserId>
  requestTypes: ReadonlyArray<PracticeRequestType>
  status: 'error' | 'pending' | 'ready'
}

/**
 * Drops type and patient filters a settled facet response proves unavailable.
 *
 * Returns the same `queryParams` reference when nothing is stale, so callers can
 * compare by identity to decide whether the URL needs replacing.
 */
export function cleanupStalePracticeRequestFacetFilters({
  facetValuesState,
  queryParams,
}: {
  facetValuesState: PracticeRequestFacetValuesState
  queryParams: PracticeRequestsRouteQueryParams
}): PracticeRequestsRouteQueryParams {
  const { isAuthorizedBaseScopeResponse, isPlaceholderData, status } =
    facetValuesState
  const cleanedRequestTypes = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.requestTypes,
    },
    selectedValues: queryParams.request_type,
  })
  const cleanedPatientIds = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.patientIds,
    },
    selectedValues: queryParams.patient_ids,
  })
  const cleanedPublisherUserIds = cleanupStaleFacetValues({
    facetValuesState: {
      isAuthorizedBaseScopeResponse,
      isPlaceholderData,
      status,
      values: facetValuesState.publisherUserIds,
    },
    selectedValues: queryParams.publisher_user_ids,
  })
  const isUnchanged =
    cleanedRequestTypes.length === queryParams.request_type.length &&
    cleanedPatientIds.length === queryParams.patient_ids.length &&
    cleanedPublisherUserIds.length === queryParams.publisher_user_ids.length
  if (isUnchanged) {
    return queryParams
  }

  return {
    ...queryParams,
    page: EmptyPracticeRequestsListParams.page,
    patient_ids: cleanedPatientIds,
    publisher_user_ids: cleanedPublisherUserIds,
    request_type: cleanedRequestTypes,
  }
}

export type PracticeRequestAssigneeListFilter = 'internal' | 'practice'

function isSameOwnerFilter({
  expectedOwners,
  owners,
}: {
  expectedOwners: ReadonlyArray<PracticeRequestOwner>
  owners: ReadonlyArray<PracticeRequestOwner>
}) {
  return (
    owners.length === expectedOwners.length &&
    expectedOwners.every((owner) => {
      return owners.includes(owner)
    })
  )
}

export function isPracticeRequestNeedsResponseOwnerFilter({
  assigneeListFilter,
  owners,
}: {
  assigneeListFilter: PracticeRequestAssigneeListFilter | null
  owners: ReadonlyArray<PracticeRequestOwner>
}) {
  if (assigneeListFilter === 'internal') {
    return isSameOwnerFilter({
      expectedOwners: NeedsJoyfulResponseOwners,
      owners,
    })
  }

  return isSameOwnerFilter({
    expectedOwners: NeedsPracticeResponseOwners,
    owners,
  })
}

export function isPracticeRequestAssignedToJoyfulHumanOwnerFilter(
  owners: ReadonlyArray<PracticeRequestOwner>,
) {
  return isSameOwnerFilter({
    expectedOwners: AssignedToJoyfulHumanOwners,
    owners,
  })
}

/** Rewrites filters the list no longer offers: `needs_response`, and Assigned to me under grouped requests. */
export function migrateLegacyPracticeRequestsQueryParams({
  assigneeListFilter,
  isGroupedPracticeRequestsEnabled,
  queryParams,
}: {
  assigneeListFilter: PracticeRequestAssigneeListFilter | null
  isGroupedPracticeRequestsEnabled: boolean
  queryParams: PracticeRequestsRouteQueryParams
}): PracticeRequestsRouteQueryParams {
  const isAssignedToMeHidden =
    isGroupedPracticeRequestsEnabled &&
    assigneeListFilter === 'practice' &&
    queryParams.assigned_to_me
  const visibleQueryParams = isAssignedToMeHidden
    ? {
        ...queryParams,
        assigned_to_me: false,
        page: EmptyPracticeRequestsListParams.page,
      }
    : queryParams
  if (!visibleQueryParams.needs_response) {
    return visibleQueryParams
  }

  return {
    ...visibleQueryParams,
    needs_response: false,
    owner:
      visibleQueryParams.owner.length > 0
        ? visibleQueryParams.owner
        : [
            ...(assigneeListFilter === 'internal'
              ? NeedsJoyfulResponseOwners
              : NeedsPracticeResponseOwners),
          ],
    page: EmptyPracticeRequestsListParams.page,
  }
}

export type PracticeRequestListQuickFilter =
  | 'all'
  | 'assigned'
  | 'drafts'
  | 'needs_response'
  | 'snoozed'
  | 'uncollectible_recommendations'

export function practiceRequestListQuickFilterQueryParams({
  assigneeListFilter,
  filter,
  isDraftsActive,
  status,
}: {
  assigneeListFilter: PracticeRequestAssigneeListFilter | null
  filter: PracticeRequestListQuickFilter
  isDraftsActive: boolean
  status: ReadonlyArray<PracticeRequestListStatus>
}): Partial<PracticeRequestsRouteQueryParams> {
  if (filter === 'needs_response') {
    return {
      assigned_to_me: false,
      owner: [
        ...(assigneeListFilter === 'internal'
          ? NeedsJoyfulResponseOwners
          : NeedsPracticeResponseOwners),
      ],
      page: 1,
      snoozed: false,
      sort_key: null,
      status: [],
      uncollectible_recommendation: false,
    }
  }

  if (filter === 'assigned') {
    return {
      assigned_to_me: false,
      owner: [...AssignedToJoyfulHumanOwners],
      page: 1,
      snoozed: false,
      sort_key: null,
      status: [],
      uncollectible_recommendation: false,
    }
  }

  if (filter === 'drafts') {
    return {
      assigned_to_me: false,
      owner: [],
      page: 1,
      snoozed: false,
      status: ['DRAFT'],
      uncollectible_recommendation: false,
    }
  }

  if (filter === 'snoozed') {
    return {
      assigned_to_me: false,
      owner: [],
      page: 1,
      snoozed: true,
      sort_key: null,
      status: [],
      uncollectible_recommendation: false,
    }
  }

  if (filter === 'uncollectible_recommendations') {
    return {
      assigned_to_me: false,
      owner: [],
      page: 1,
      snoozed: false,
      status: isDraftsActive ? [] : [...status],
      uncollectible_recommendation: true,
    }
  }

  return {
    assigned_to_me: false,
    owner: [],
    page: 1,
    snoozed: false,
    status: isDraftsActive
      ? []
      : normalizePracticeRequestListStatusFilter(
          status.filter((value) => {
            const isClosed = value === 'CLOSED'
            const isDraft = value === 'DRAFT'
            return !isClosed && !isDraft
          }),
        ),
    uncollectible_recommendation: false,
  }
}

/** List filter fields shared by the list request and its facet request. */
export function buildPracticeRequestsListFilterQuery({
  assigneeListFilter,
  currentUserId,
  draftListing,
  queryParams,
}: {
  assigneeListFilter: PracticeRequestAssigneeListFilter | null
  currentUserId: string
  draftListing: PracticeRequestListDraftListing
  queryParams: PracticeRequestsRouteQueryParams
}) {
  const isNeedsResponse = isPracticeRequestNeedsResponseOwnerFilter({
    assigneeListFilter,
    owners: queryParams.owner,
  })
  const selectedOwner = queryParams.owner.at(0)
  const owner =
    !isNeedsResponse && queryParams.owner.length === 1
      ? selectedOwner
      : undefined
  return {
    ...practiceRequestListStatusQueryParams({
      draftListing,
      includeClosed: queryParams.include_closed,
      requestedStatuses: queryParams.status,
    }),
    owner,
    patient_ids:
      queryParams.patient_ids.length > 0 ? queryParams.patient_ids : undefined,
    pending_response: isNeedsResponse || undefined,
    practice_assignee_user_id:
      assigneeListFilter === 'practice' && queryParams.assigned_to_me
        ? currentUserId
        : undefined,
    publisher_user_ids:
      queryParams.publisher_user_ids.length > 0
        ? queryParams.publisher_user_ids
        : undefined,
    query: queryParams.query.trim() || undefined,
    request_types:
      queryParams.request_type.length > 0
        ? queryParams.request_type
        : undefined,
    snoozed: queryParams.snoozed || undefined,
    themes: queryParams.themes.length > 0 ? queryParams.themes : undefined,
    uncollectible_recommendation:
      queryParams.uncollectible_recommendation || undefined,
  }
}

export function buildPracticeRequestsListRequestQuery(
  params: Parameters<typeof buildPracticeRequestsListFilterQuery>[0],
) {
  const { queryParams } = params
  return {
    ...buildPracticeRequestsListFilterQuery(params),
    limit: Math.max(1, queryParams.limit),
    page: Math.max(1, queryParams.page),
    sort_key: queryParams.sort_key ?? undefined,
    sort_order: queryParams.sort_order,
  }
}

type PracticeRequestThemeGroup =
  components['schemas']['PracticeRequestThemeGroup']

export interface PracticeRequestThemeSessionTheme {
  count: number
  requestType: PracticeRequestType
  theme: string
}

/**
 * Needs practice response works one theme at a time: start on the selected theme when it still
 * needs a response, otherwise on the largest theme, and move on
 * once the current theme has no request left to answer. `redirectQueryParams` is set when the
 * URL must move to another theme, or drop a theme that no longer needs a response.
 */
export function resolvePracticeRequestThemeSession<
  TQueryParams extends Pick<
    PracticeRequestsRouteQueryParams,
    'page' | 'request_type' | 'themes'
  >,
>({
  hasSettledFacetResponse,
  isFacetResponseError,
  isThemeSession,
  queryParams,
  themeGroups,
}: {
  hasSettledFacetResponse: boolean
  isFacetResponseError: boolean
  isThemeSession: boolean
  queryParams: TQueryParams
  themeGroups: ReadonlyArray<PracticeRequestThemeGroup>
}) {
  const queue = buildPracticeRequestThemeSessionQueue(themeGroups)
  const currentIndex = findPracticeRequestThemeSessionIndex({
    queryParams,
    queue,
  })
  const isOnSessionTheme = currentIndex !== -1
  const startTheme =
    isThemeSession && hasSettledFacetResponse && !isOnSessionTheme
      ? queue.at(0)
      : undefined
  const shouldClearStaleTheme =
    isThemeSession &&
    hasSettledFacetResponse &&
    queue.length === 0 &&
    queryParams.themes.length > 0
  const redirectQueryParams = startTheme
    ? {
        ...queryParams,
        page: 1,
        request_type: [startTheme.requestType],
        themes: [startTheme.theme],
      }
    : shouldClearStaleTheme
      ? { ...queryParams, page: 1, request_type: [], themes: [] }
      : null
  const isResolving =
    isThemeSession &&
    !isOnSessionTheme &&
    !isFacetResponseError &&
    (!hasSettledFacetResponse || queue.length > 0)

  return { currentIndex, isResolving, queue, redirectQueryParams }
}

/** Themes most requests first; ties keep the server's type and theme order. */
function buildPracticeRequestThemeSessionQueue(
  themeGroups: ReadonlyArray<PracticeRequestThemeGroup>,
): Array<PracticeRequestThemeSessionTheme> {
  const themes = themeGroups.flatMap((themeGroup) => {
    return themeGroup.themes.map((themeFacet) => {
      return {
        count: themeFacet.count,
        requestType: themeGroup.request_type,
        theme: themeFacet.theme,
      }
    })
  })
  return themes.sort((first, second) => {
    return second.count - first.count
  })
}

/** Position of the URL's single type and theme in the session queue, or -1. */
function findPracticeRequestThemeSessionIndex({
  queryParams,
  queue,
}: {
  queryParams: Pick<PracticeRequestsRouteQueryParams, 'request_type' | 'themes'>
  queue: ReadonlyArray<PracticeRequestThemeSessionTheme>
}): number {
  const isSingleTheme =
    queryParams.request_type.length === 1 && queryParams.themes.length === 1
  if (!isSingleTheme) {
    return -1
  }

  return queue.findIndex((sessionTheme) => {
    return (
      sessionTheme.requestType === queryParams.request_type.at(0) &&
      sessionTheme.theme === queryParams.themes.at(0)
    )
  })
}

export function usePracticeRequestsRouteQueryParams() {
  return useQueryStates(PracticeRequestsRouteQueryMap, { history: 'push' })
}

export const serializePracticeRequestsRouteQueryParams = createSerializer(
  PracticeRequestsRouteQueryMap,
)
